// Sprint 6-organs §0/§6 — чистые хелперы органного разреза «Маркеров».
// Логика тайла/сводки/разреженности вынесена сюда pure-функциями (нет @testing-library),
// покрывается organView.test.ts (паттерн biomarkerStatus.test.ts).
//
// ⚠️ Позиционирование (§0, TC-E(fe)-c): copy — «показатели, связанные с …», НЕ
// «состояние/диагноз органа»; сводка — фактический счёт статусов, НЕ балл («78/100»).
// Цвет — через общий displayTier-словарь (biomarkerStatus.ts), не второй словарь.

import type { DisplayTier } from '@/lib/biomarkerStatus';
import type { OrganStatus } from '@/api/types';

/** Подзаголовок органного разреза/детали — нейтральная формулировка §0. */
export const ORGAN_RELATED_CAPTION = 'Показатели, связанные с этим органом';

/**
 * OrganStatus (backend headline/counts) → дисплей-тир общего цветового словаря.
 * out_of_range→bad 🔴, attention→attention 🟠, in_range→norm 🟢. Второго
 * цветового источника не заводим (amber зарезервирован под 🟠).
 */
export function organStatusToTier(status: OrganStatus): DisplayTier {
  if (status === 'out_of_range') return 'bad';
  if (status === 'attention') return 'attention';
  return 'norm';
}

function pluralizePokazatel(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 'показатель';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return 'показателя';
  return 'показателей';
}

/**
 * Счётчик разреженности тайла (§6.3): «X из Y показателей». Знаменатель Y —
 * markers_total (verified+оцениваемые, Q4). Генитив-множественное «показателей»
 * в конструкции «из Y …» — грамматически корректно и даёт copy-маркер «показател».
 */
export function organMarkersCountLabel(withData: number, total: number): string {
  return `${withData} из ${total} показателей`;
}

/**
 * Фактический счёт статусов (§0): «3 в норме, 1 требует внимания». НЕ балл органа.
 * Порядок — от тревожного к спокойному (вне нормы → внимание → норма); нули опускаем.
 */
export function statusCountsSummary(counts: {
  out_of_range: number;
  attention: number;
  in_range: number;
}): string {
  const parts: string[] = [];
  if (counts.out_of_range > 0) parts.push(`${counts.out_of_range} вне нормы`);
  if (counts.attention > 0) parts.push(`${counts.attention} требует внимания`);
  if (counts.in_range > 0) parts.push(`${counts.in_range} в норме`);
  return parts.join(', ');
}

/**
 * Подпись сворачиваемого блока «ещё не загружено» (§6.3, D3, engagement-хук):
 * «Ещё N показателей — загрузите анализ».
 */
export function organMissingLabel(count: number): string {
  return `Ещё ${count} ${pluralizePokazatel(count)} — загрузите анализ`;
}
